import React, {
  forwardRef,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import i18n from 'i18next'
import { useTranslation } from 'react-i18next'
import { Button, MenuItem, Select, Switch } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { useForm, Controller } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import { isEqual, omit } from 'lodash'
import {
  BankTransfer,
  CallMethodProps,
  DepartmentSettingsProps,
  IndividualReservationSettingProps,
  MedicalInstitutionProps,
  RegularReservationSettingProps,
  ReservationDeadlineTimings,
  reservationDeadlineTimings,
} from 'types/med'
import { CallMethodVideoAppNames, ReservationTimeUnit } from 'types/common'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { StackItemTextField } from 'ui/shorthands/StackItemTextField'
import validationSchema from 'ui/shorthands/validationSchema'
import RegularReservationFrameSetting from 'ui/organisms/ReservationFrameSetting/RegularReservationFrameSetting'
import { QuestionTooltipWithLabel } from 'ui/shorthands/QuestionTooltip'
import IndividualReservationSettingList from 'ui/organisms/ReservationFrameSetting/IndividualReservationSettingList'
import CloseDateSettingList from 'ui/organisms/CloseDateSettingList'
import { StackItemRadioSelect } from 'ui/shorthands/StackItemRadioSelect'
import { Text1, H2, H3, Caption } from 'ui/shorthands/Texts'
import { StackItem } from 'ui/layout/StackItem'
import SettlementSettingsCheckBoxes from 'ui/partials/SettlementSettingsCheckBoxes'
import { CallMethodEdit } from './CallMethod'
import { AllTimeStringArray } from 'ui/utils/AvailableDateTimes'
import { callSettingHeaderContent } from './DepartmentRead'
import { useStylesInnerAdminContentWrapper } from 'ui/layout/AdminContentWrapper'
import {
  addHourString,
  reservationFramesArray15To30,
  reservationFramesArray30To15,
} from 'ui/organisms/ReservationFrameSetting/reservationFramesUtils'

export const useStylesDepartment = makeStyles()((theme: Theme) => ({
  content: {
    paddingTop: theme.spacing(1.5),
  },
  category: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    '&:first-child': {
      marginTop: 0,
    },
  },
  parentHeader: {
    marginBottom: theme.spacing(3),
  },
  subHeader: {
    marginTop: theme.spacing(1),
    marginBottom: 0,
  },
  preWrap: {
    whiteSpace: 'pre-wrap',
  },
  noWrapper: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
    '&:first-child': {
      marginTop: 0,
    },
  },
  select: {
    minWidth: 240,
  },
  timeSelect: {
    minWidth: 115,
  },
  timeSettingSelectText: {
    display: 'inline-block',
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  switchWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  hide: {
    display: 'none',
  },
  controlButtons: {
    marginLeft: theme.spacing(-2),
  },
}))

export const reservablePeriods: { label: string; value: number }[] = [
  ...Array(12),
].map((_, i) => {
  return {
    label: `${i + 1}${i18n.t('週間', { count: i + 1 })}`,
    value: (i + 1) * 7,
  }
})

export const DepartmentStaticItems = () => (
  <>
    <StackItem label={i18n.t('氏名')}>
      <Text1>{i18n.t('必須')}</Text1>
    </StackItem>
    <StackItem label={i18n.t('生年月日')}>
      <Text1>{i18n.t('必須')}</Text1>
    </StackItem>
    <StackItem label={i18n.t('電話番号')}>
      <Text1>{i18n.t('必須')}</Text1>
    </StackItem>
    <StackItem label={i18n.t('メールアドレス')}>
      <Text1>
        {i18n.t(
          'メールアドレス認証での予約の場合は必須（SMS認証での予約の場合は使用しない）'
        )}
      </Text1>
    </StackItem>
  </>
)

const getDefaultValues = ({
  name,
  supplementary_text,
  ...rest
}: DepartmentSettingsProps): DepartmentSettingsProps => {
  return {
    ...rest,
    // NOTE: 名称未設定/未設定をそれぞれ初期値とみなし空白にする
    name: name === i18n.t('名称未設定') ? '' : name,
    supplementary_text:
      supplementary_text === i18n.t('未設定') ? '' : supplementary_text,
  }
}

type DepartmentEditPropsWithoutRef = {
  /** 医療機関設定 */
  med: MedicalInstitutionProps
  /** 振込先設定 */
  bank?: BankTransfer
  /** セットする既存値 */
  defaultValues: DepartmentSettingsProps
  /** 未完了の予約が存在するか */
  hasUnFinishedCallReservation?: boolean
  /** ビデオ通話アプリ接続時に発火 */
  onClickConnectVideoApp: (app: CallMethodVideoAppNames) => Promise<void>
  /** ビデオ通話アプリ接続解除時に発火 */
  onClickDisconnectVideoApp: (app: CallMethodVideoAppNames) => Promise<void>
  /** 保存時 */
  onSubmit: (data: DepartmentSettingsProps) => Promise<void>
  /** 削除時 */
  onClickDelete: () => void
  /** 削除できない科目かどうか */
  unDeletable?: boolean
  /** 編集のキャンセル */
  onClickCancel: () => void
  /** アプリ接続結果 */
  connectVideoAppResult?: CallMethodVideoAppNames | { error: string }
  /** 子要素 */
  children?: React.ReactNode
}

export type DepartmentEditRefProps = {
  changed: () => boolean
}

export type DepartmentEditProps = ForwardRefExoticComponent<
  PropsWithoutRef<DepartmentEditPropsWithoutRef> &
    RefAttributes<DepartmentEditRefProps>
>

// eslint-disable-next-line react/display-name
const DepartmentEdit: DepartmentEditProps = forwardRef(
  (
    {
      med,
      bank,
      defaultValues,
      hasUnFinishedCallReservation,
      onClickConnectVideoApp,
      onClickDisconnectVideoApp,
      onSubmit,
      onClickDelete,
      unDeletable,
      onClickCancel,
      children,
      connectVideoAppResult,
    }: DepartmentEditPropsWithoutRef,
    ref
  ) => {
    const { t } = useTranslation()
    const { classes } = useStylesDepartment()
    const layoutClasses = useStylesInnerAdminContentWrapper().classes

    const schema = yup.object().shape({
      name: validationSchema.textRequired(),
      supplementary_text: yup.string().trim().max(1000).required(),
      free_description_supplement: yup
        .string()
        .when('free_description_field_setting', {
          is: 'not',
          then: yup.string().trim().max(1000).nullable(),
          otherwise: yup.string().trim().max(1000).required(),
        }),
      attachment_image_supplement: yup
        .string()
        .when('attachment_image_setting', {
          is: 'not',
          then: validationSchema.text().nullable(),
          otherwise: validationSchema.textRequired(),
        }),
      reservation_message: yup.string().when('call_methods', {
        is: (methods: CallMethodProps[]) => !!methods.find((m) => m.is_active),
        then: validationSchema.text().nullable(),
        otherwise: validationSchema.textRequired().max(400),
      }),
      information_for_reservation: yup.string().trim().max(1000).nullable(),
      is_open: yup.boolean(),
      use_in_person_consultation: yup.boolean(),
      call_methods: yup.array(),
      reservation_frame_period_minutes: yup.number().required(),
    })

    const {
      handleSubmit,
      control,
      getValues,
      setValue,
      formState: { isValid, isDirty, errors },
      trigger,
      watch,
    } = useForm<FieldValues>({
      defaultValues: getDefaultValues(defaultValues),
      resolver: yupResolver(schema),
      mode: 'onChange',
    })

    const [updating, setUpdating] = useState<boolean>(false)
    const watchCallMethods: CallMethodProps[] = watch('call_methods')
    const watchReservationFrameStartTime = watch('reservation_frame_start_time')
    const watchReservationFrameEndTime = watch('reservation_frame_end_time')
    const watchReservationFrameUnit = watch('reservation_frame_period_minutes')

    const allTimeString = new AllTimeStringArray(
      watchReservationFrameUnit || 30
    )

    useEffect(() => {
      if (watchCallMethods?.find((m) => m.is_active)) {
        setValue('reservation_message', '')
      }
      trigger('reservation_message')
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchCallMethods])

    const onChangeUnitHandler = (unit: ReservationTimeUnit) => {
      const [
        individualReservationSettings,
        regularReservationSettings,
        frameStartTime,
        frameEndTime,
      ] = getValues([
        'individual_reservation_settings',
        'regular_reservation_settings',
        'reservation_frame_start_time',
        'reservation_frame_end_time',
      ])

      if (!individualReservationSettings && !regularReservationSettings) {
        return
      }

      let individualConverted: IndividualReservationSettingProps[] = []
      let regularConverted: RegularReservationSettingProps[] = []

      if (unit === 15) {
        individualConverted =
          reservationFramesArray30To15<IndividualReservationSettingProps>(
            individualReservationSettings
          )
        regularConverted =
          reservationFramesArray30To15<RegularReservationSettingProps>(
            regularReservationSettings
          )
      }
      if (unit === 30) {
        individualConverted =
          reservationFramesArray15To30<IndividualReservationSettingProps>(
            individualReservationSettings
          )
        regularConverted =
          reservationFramesArray15To30<RegularReservationSettingProps>(
            regularReservationSettings
          )

        // NOTE: 開始、終了時刻を広げる方向に変換

        if (frameStartTime?.endsWith('15')) {
          setValue(
            'reservation_frame_start_time',
            frameStartTime.replace(':15', ':00')
          )
        }
        if (frameStartTime?.endsWith('45')) {
          setValue(
            'reservation_frame_start_time',
            frameStartTime.replace(':45', ':30')
          )
        }

        if (frameEndTime?.endsWith('15')) {
          setValue(
            'reservation_frame_end_time',
            frameEndTime.replace(':15', ':30')
          )
        }
        if (frameEndTime?.endsWith('45')) {
          setValue(
            'reservation_frame_end_time',
            [addHourString(frameEndTime.split(':')[0], 1), '00'].join(':')
          )
        }
      }
      setValue('individual_reservation_settings', individualConverted)
      setValue('regular_reservation_settings', regularConverted)
    }

    useImperativeHandle(ref, () => ({
      //NOTE: ビデオ通話アプリの接続だけこのスコープ外で実行されるためcall_methodsも除外
      changed: () =>
        !isEqual(
          omit(defaultValues, 'id', 'display_order', 'call_methods'),
          omit(getValues(), 'call_methods')
        ),
    }))

    return (
      <div className={layoutClasses.root}>
        <form
          onSubmit={handleSubmit((data) => {
            setUpdating(true)
            onSubmit(data as DepartmentSettingsProps).finally(() =>
              setUpdating(false)
            )
          })}
        >
          <div className={layoutClasses.main}>
            <div className={layoutClasses.content}>
              <div className={classes.content}>
                <H2>受付科目</H2>
                <div className={classes.category}>
                  <StackItemTextField
                    name={'name'}
                    label={t('科目名')}
                    control={control}
                    errorMessage={errors?.name?.message}
                    placeholder={t('placeholder.発熱外来 田中医師')}
                    fullWidth
                  />
                  <StackItemTextField
                    name={'supplementary_text'}
                    label={
                      <QuestionTooltipWithLabel label={t('科目説明文')}>
                        {t('申込者が予約する画面に表示されます。')}
                      </QuestionTooltipWithLabel>
                    }
                    placeholder={t('placeholder.初診で発熱のある方はこちら')}
                    control={control}
                    errorMessage={errors?.supplementary_text?.message}
                    fullWidth
                    multiline
                    alignTop
                    descriptionBelow={t(
                      'スマートフォンでの閲覧を考慮し、100文字以内を推奨しています。'
                    )}
                  />
                  <StackItem
                    label={
                      <QuestionTooltipWithLabel label={t('ステータス')}>
                        {t('公開にすると予約フォームに表示されます。')}
                      </QuestionTooltipWithLabel>
                    }
                  >
                    <div className={classes.switchWrapper}>
                      <div>{t('非公開')}</div>
                      <Controller
                        name={'is_open'}
                        control={control}
                        render={({ field }) => (
                          <Switch
                            {...field}
                            checked={field.value}
                            color="primary"
                          />
                        )}
                      />
                      <div>{t('公開')}</div>
                    </div>
                  </StackItem>
                  <StackItemTextField
                    name={'information_for_reservation'}
                    label={
                      <QuestionTooltipWithLabel
                        label={t('予約完了メールに記載する連絡事項(任意)')}
                      >
                        {t(
                          '予約完了メールに連絡事項として表示されます。Web問診のリンク、診療当日準備するものなどを予約者に伝えることができます。'
                        )}
                      </QuestionTooltipWithLabel>
                    }
                    control={control}
                    errorMessage={errors?.information_for_reservation?.message}
                    fullWidth
                    multiline
                    alignTop
                    descriptionBelow={t(
                      'スマートフォンでの閲覧を考慮し、100文字以内を推奨しています。'
                    )}
                  />
                </div>
                <H2 className={classes.parentHeader}>{t('予約カレンダー')}</H2>
                <H3 className={classes.subHeader}>{t('設定')}</H3>
                <div className={classes.category}>
                  <StackItem label={t('予約受付期間')}>
                    <Controller
                      name={'reservable_period'}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          variant="outlined"
                          displayEmpty
                          defaultValue="14"
                          className={classes.select}
                        >
                          {reservablePeriods.map((p) => (
                            <MenuItem key={p.label} value={p.value}>
                              {p.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </StackItem>
                  <StackItem label={t('予約受付締切')}>
                    <Controller
                      name={'reservation_deadline_timing'}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          variant="outlined"
                          className={classes.select}
                        >
                          {Object.keys(reservationDeadlineTimings).map(
                            (key: string) => (
                              <MenuItem key={`select-${key}`} value={key}>
                                {t(
                                  reservationDeadlineTimings[
                                    key as ReservationDeadlineTimings
                                  ]
                                )}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      )}
                    />
                  </StackItem>
                  <StackItemRadioSelect<number>
                    label={
                      <QuestionTooltipWithLabel label={t('診療時間枠')}>
                        {t(
                          '設定を変更すると、既存予約の開始時刻が変更される場合があります。変更後は予約をご確認ください。'
                        )}
                      </QuestionTooltipWithLabel>
                    }
                    values={[
                      {
                        label: `15${t('分')}`,
                        value: 15,
                      },
                      {
                        label: `30${t('分')}`,
                        value: 30,
                      },
                    ]}
                    name={'reservation_frame_period_minutes'}
                    control={control}
                    onChange={(value) => {
                      onChangeUnitHandler(Number(value) as ReservationTimeUnit)
                      return value
                    }}
                  />
                </div>
                <H3 className={classes.subHeader}>{t('定期予約')}</H3>
                <div className={classes.category}>
                  <StackItem label={t('予約受付時間')}>
                    <Controller
                      name={'reservation_frame_start_time'}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          variant="outlined"
                          className={classes.timeSelect}
                        >
                          {allTimeString
                            .start(watchReservationFrameEndTime)
                            .map((time: string) => (
                              <MenuItem
                                key={`select-start-${time}`}
                                value={time}
                              >
                                {time}
                              </MenuItem>
                            ))}
                        </Select>
                      )}
                    />
                    <span className={classes.timeSettingSelectText}>
                      {t('から')}
                    </span>
                    <Controller
                      name={'reservation_frame_end_time'}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          variant="outlined"
                          className={classes.timeSelect}
                        >
                          {allTimeString
                            .end(watchReservationFrameStartTime)
                            .map((time: string) => (
                              <MenuItem key={`select-end-${time}`} value={time}>
                                {time}
                              </MenuItem>
                            ))}
                        </Select>
                      )}
                    />
                    <span className={classes.timeSettingSelectText}>
                      {t('まで')}
                    </span>
                  </StackItem>
                  <Controller
                    name={'regular_reservation_settings'}
                    control={control}
                    render={({ field }) => (
                      <RegularReservationFrameSetting
                        {...field}
                        unit={watchReservationFrameUnit}
                        reservationSettings={field.value}
                        from={watchReservationFrameStartTime || '08:00'}
                        to={watchReservationFrameEndTime || '20:00'}
                        onChange={(value) => {
                          setValue('regular_reservation_settings', value, {
                            shouldDirty: true,
                          })
                        }}
                      />
                    )}
                  />
                </div>
                <div className={classes.noWrapper}>
                  <Controller
                    name={'individual_reservation_settings'}
                    control={control}
                    render={({ field }) => (
                      <IndividualReservationSettingList
                        unit={watchReservationFrameUnit}
                        {...field}
                        reservationSettings={field.value}
                        onChange={(value) => {
                          setValue('individual_reservation_settings', value, {
                            shouldDirty: true,
                          })
                        }}
                      />
                    )}
                  />
                </div>
                <div className={classes.noWrapper}>
                  <Controller
                    name={'close_date_settings'}
                    control={control}
                    render={({ field }) => (
                      <CloseDateSettingList
                        {...field}
                        closeDateSettings={field.value}
                        onChange={(value) => {
                          setValue('close_date_settings', value, {
                            shouldDirty: true,
                          })
                        }}
                      />
                    )}
                  />
                </div>
                <H2>{t('ヒアリング項目')}</H2>
                <div className={classes.category}>
                  <StackItemRadioSelect
                    label={t('自由記述欄')}
                    name={'free_description_field_setting'}
                    control={control}
                    onChange={([event]) => {
                      // NOTE:値が変わってから再度バリデーション
                      setTimeout(
                        () => trigger('free_description_supplement'),
                        0
                      )
                      return event
                    }}
                  />
                  <StackItemTextField
                    name={'free_description_supplement'}
                    label={t('自由記述欄補足文')}
                    control={control}
                    errorMessage={errors?.free_description_supplement?.message}
                    fullWidth
                    borderBottom
                    multiline
                    alignTop
                    descriptionBelow={t(
                      'スマートフォンでの閲覧を考慮し、100文字以内を推奨しています。'
                    )}
                  />
                  <StackItemRadioSelect
                    label={t('ファイル添付')}
                    name={'attachment_image_setting'}
                    control={control}
                    onChange={([event]) => {
                      // NOTE:値が変わってから再度バリデーション
                      setTimeout(
                        () => trigger('attachment_image_supplement'),
                        0
                      )
                      return event
                    }}
                  />
                  <StackItemTextField
                    name={'attachment_image_supplement'}
                    label={t('ファイル添付補足文')}
                    control={control}
                    errorMessage={errors?.attachment_image_supplement?.message}
                    fullWidth
                  />
                </div>
                <H2>{t('受診方法')}</H2>
                <div className={classes.category}>
                  <StackItem label={t('対面受診の選択肢')} alignTop={'24px'}>
                    <div className={classes.switchWrapper}>
                      <div>{t('使用しない')}</div>
                      <Controller
                        name={'use_in_person_consultation'}
                        control={control}
                        render={({ field }) => (
                          <Switch
                            {...field}
                            checked={field.value}
                            color="primary"
                          />
                        )}
                      />
                      <div>{t('使用する')}</div>
                    </div>
                    <div>
                      <Caption>
                        {t(
                          '「使用する」の場合、予約フォームに受診方法が表示され、オンラインか対面を選択できるようになります。'
                        )}
                      </Caption>
                    </div>
                  </StackItem>
                </div>
                <H2>{callSettingHeaderContent}</H2>
                <div className={classes.category}>
                  <CallMethodEdit
                    value={watchCallMethods}
                    onClickConnect={onClickConnectVideoApp}
                    onClickDisConnect={onClickDisconnectVideoApp}
                    hasUnFinishedCallReservation={hasUnFinishedCallReservation}
                    connectVideoAppResult={connectVideoAppResult}
                    onChange={(data) => {
                      if (!isEqual(data, watchCallMethods)) {
                        setValue('call_methods', data, { shouldDirty: true })
                      }
                    }}
                  />
                  <Controller
                    name={'call_methods'}
                    control={control}
                    render={({ field }) => <input {...field} type="hidden" />}
                  />
                  {!watchCallMethods?.find((m) => m.is_active) && (
                    <StackItemTextField
                      label={
                        <QuestionTooltipWithLabel label={t('連絡事項')}>
                          {t(
                            '通話を使用しない場合、予約時刻に行っていただく内容を記載してください。予約完了メール内に表示されます。'
                          )}
                        </QuestionTooltipWithLabel>
                      }
                      placeholder={t(
                        'placeholder.予約時間になりましたら、受付にお声かけください。'
                      )}
                      name={'reservation_message'}
                      errorMessage={errors?.reservation_message?.message}
                      control={control}
                      multiline
                      textCounter={400}
                      fullWidth
                      alignTop
                    />
                  )}
                </div>

                <H2>申込者</H2>
                <div className={classes.category}>
                  <DepartmentStaticItems />
                  <StackItemRadioSelect
                    label={t('住所')}
                    name={'applicant_address_setting'}
                    control={control}
                  />
                  <StackItemRadioSelect
                    label={t('診察券番号')}
                    name={'registration_card_number_setting'}
                    control={control}
                  />
                  <StackItemRadioSelect
                    label={t('保険証・マイナ保険証')}
                    name={'insurance_card_setting'}
                    control={control}
                  />
                  <StackItemRadioSelect
                    label={t('保険証以外の証書')}
                    name={'certificate_setting'}
                    control={control}
                  />
                  <StackItemRadioSelect
                    label={t('本人確認書類')}
                    name={'identification_document_setting'}
                    control={control}
                  />
                </div>
                <H2>{t('決済')}</H2>
                <div className={classes.category}>
                  <StackItem
                    label={
                      <QuestionTooltipWithLabel label={t('決済方法')}>
                        {t('申込者にメールでお知らせします。')}
                      </QuestionTooltipWithLabel>
                    }
                    alignTop
                  >
                    <Controller
                      name={'settlement_settings'}
                      control={control}
                      render={({ field }) => (
                        <SettlementSettingsCheckBoxes
                          {...field}
                          med={med}
                          bank={bank}
                          value={field.value}
                          onChange={(value) => {
                            if (!isEqual(field.value, value)) {
                              setValue('settlement_settings', value, {
                                shouldDirty: true,
                              })
                            }
                          }}
                        />
                      )}
                    />
                  </StackItem>
                </div>
              </div>
            </div>
            <div className={layoutClasses.controlBar}>
              <div className={classes.controlButtons}>
                <Button
                  onClick={onClickDelete}
                  color={'secondary'}
                  disabled={unDeletable}
                >
                  {t('削除')}
                </Button>
                <Button onClick={onClickCancel} color={'primary'}>
                  {t('キャンセル')}
                </Button>
              </div>
              <div>
                <Button
                  type={'submit'}
                  variant={'contained'}
                  color={'primary'}
                  disabled={!isValid || !isDirty || updating}
                >
                  {t('保存')}
                </Button>
              </div>
            </div>
          </div>
        </form>
        {children}
      </div>
    )
  }
)

export default DepartmentEdit
